import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const CONCERN_AREAS = [
  'addiction',
  'work_stress',
  'academic_stress',
  'grief',
  'heartbreak',
  'anxiety',
  'depression',
  'burnout',
  'relationship_issues',
  'life_transitions'
];

const RequestMatchModal = ({ facilitator, onClose, onSubmit }) => {
  const [message, setMessage] = useState('');
  const [concernAreas, setConcernAreas] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleConcernArea = (area) => {
    if (concernAreas.includes(area)) {
      setConcernAreas(concernAreas.filter(a => a !== area));
    } else {
      setConcernAreas([...concernAreas, area]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      alert('Please write a message explaining why you need support');
      return;
    }

    if (concernAreas.length === 0) {
      alert('Please select at least one area of concern');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        message: message.trim(),
        concern_areas: concernAreas
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid #333'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: '#1a1a1a',
          zIndex: 1
        }}>
          <h2 style={{ color: '#FFB6C1', fontSize: '1.5rem', margin: 0 }}>
            Request Match
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {/* Facilitator Info */}
          <div style={{
            backgroundColor: '#000',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: '#FFB6C1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#000'
              }}>
                {facilitator.user?.full_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <h3 style={{ color: '#FFB6C1', margin: 0, marginBottom: '0.25rem' }}>
                  {facilitator.user?.full_name}
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {facilitator.specializations?.map((spec, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '0.125rem 0.5rem',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #FFB6C1',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        color: '#FFB6C1'
                      }}
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Message */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#FFB6C1',
              fontWeight: '600'
            }}>
              Why do you need support? *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Share what you're going through and how you'd like support..."
              rows={5}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#000',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                resize: 'vertical'
              }}
              required
            />
            <p style={{ 
              marginTop: '0.5rem', 
              fontSize: '0.85rem', 
              color: '#999' 
            }}>
              Be honest and open. This helps the facilitator understand how to best support you.
            </p>
          </div>

          {/* Concern Areas */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              color: '#FFB6C1',
              fontWeight: '600'
            }}>
              Areas of Concern * (Select all that apply)
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '0.75rem'
            }}>
              {CONCERN_AREAS.map(area => (
                <label
                  key={area}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    backgroundColor: concernAreas.includes(area) ? '#FFB6C120' : '#000',
                    border: `1px solid ${concernAreas.includes(area) ? '#FFB6C1' : '#333'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={concernAreas.includes(area)}
                    onChange={() => toggleConcernArea(area)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ 
                    color: concernAreas.includes(area) ? '#FFB6C1' : '#ccc',
                    fontSize: '0.9rem',
                    textTransform: 'capitalize'
                  }}>
                    {area.replace('_', ' ')}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#000',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#ccc',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#FFB6C1',
                border: 'none',
                borderRadius: '8px',
                color: '#000',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} /> 
              {submitting ? ' Sending...' : ' Send Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestMatchModal;
